import * as React from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import Checkbox from "expo-checkbox";
import { Stack, useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { Controller, useForm } from "react-hook-form";
import slugify from "slugify";
import { z } from "zod";

import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { SaveFormBreadcrumbs } from "~/components/SaveForm/Breadcrumbs";
import { useSaveFormContext } from "~/components/SaveForm/shared";
import { SelectablePill } from "~/components/SelectablePill";
import { colors } from "~/colors";
import { icons, isWebDesktop } from "~/lib/constants";
import { generateObjectId } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { Form } from "~/ui/Form";
import { ListContainer, ListItem } from "~/ui/List";
import { TextInput } from "~/ui/TextInput";

export default observer(function SaveFormSelectLocation() {
  const { store: account } = useAccountStore();
  const { handleSubmit, setValue, formState } = useSaveFormContext();
  const { push } = useRouter();

  React.useEffect(() => {
    setValue("step", "selectLocation");
  }, [setValue]);

  return (
    <>
      <ScrollView>
        <View className="gap-y-4 px-4 py-2">
          <ResponsiveRenderer
            webDesktop={<SaveFormBreadcrumbs />}
            default={
              <Stack.Screen
                options={{
                  headerTitle: `Select a Workspace`,
                }}
              />
            }
          />
          <View />
          {account.spaces.size > 0 && (
            <ListContainer title="">
              {account.spacesAsArrayWithoutBacklog.map((space) => {
                return <RenderSpace key={space.id} spaceId={space.id} />;
              })}
            </ListContainer>
          )}
          {/* {formState.errors.location && (
            <Text className="text-red-500">
              {String(formState.errors.location?.message)}
            </Text>
          )} */}

          {formState.errors.space?.id && (
            <Text className="text-red-500">
              {String(formState.errors.space.id?.message)}
            </Text>
          )}

          <View />

          <Button
            onPress={handleSubmit(() => {
              if (isWebDesktop()) {
                return setValue("step", "form");
              }
              push({
                pathname: "/(app)/(authenticated)/[account]/link/form",
                params: {
                  account: account.slug,
                },
              });
            })}
          >
            Continue
          </Button>
          <View />
        </View>
      </ScrollView>
    </>
  );
});

const RenderCollections = observer(function RenderCollections({
  spaceId,
}: {
  spaceId: string;
}) {
  const { store: account } = useAccountStore();
  const { control, setValue, getValues } = useSaveFormContext();

  return (
    <View className="ml-4 pt-4">
      <View className="flex-row items-center gap-1">
        <Feather
          name={icons.collection.web}
          size={16}
          className="text-text-secondary pl-4"
        />
        <Text className="text-text-secondary font-semibold uppercase tracking-wide">
          Collections
        </Text>
      </View>
      <ListContainer>
        <ListItem>
          <NewCollectionOrTagForm
            type="collection"
            onSubmit={(data) => {
              const existingCollection = account
                .spaceById(spaceId)
                ?.collectionsAsArray.find((c) => c.title === data.title);
              if (existingCollection) {
                setValue(
                  "space.collections",
                  [existingCollection.id].concat(
                    getValues("space.collections"),
                  ),
                );
                return;
              }

              const collectionId = generateObjectId();
              account.createCollection({
                set: {
                  id: collectionId,
                  title: data.title,
                  slug: slugify(
                    `${data.title}-${generateObjectId().substring(0, 5)}`,
                  ),
                },
                spaceId,
              });

              setValue(
                "space.collections",
                [collectionId].concat(getValues("space.collections")),
              );
            }}
          />
        </ListItem>
        {account.spaceById(spaceId)?.collectionsAsArray?.map((collection) => {
          return (
            <Controller
              key={collection.id}
              name="space.collections"
              control={control}
              render={({ field }) => {
                const isChecked = field.value?.includes(collection.id);
                return (
                  <View key={collection.id}>
                    <ListItem asChild>
                      <Pressable
                        role="button"
                        onPress={() => {
                          if (isChecked) {
                            field.onChange(
                              field.value?.filter((c) => c !== collection.id),
                            );
                          } else {
                            field.onChange(
                              field.value?.concat([collection.id]),
                            );
                          }
                        }}
                        className="flex-row"
                      >
                        <View className="flex-row items-center gap-2">
                          <Checkbox
                            value={isChecked}
                            color={
                              isChecked
                                ? colors.button.background.primary
                                : undefined
                            }
                          />
                          <Text className="text-base text-white">
                            {collection.title ?? collection.slug}
                          </Text>
                        </View>
                      </Pressable>
                    </ListItem>
                  </View>
                );
              }}
            />
          );
        })}
      </ListContainer>
    </View>
  );
});

const RenderTags = observer(function RenderTags({
  spaceId,
}: {
  spaceId: string;
}) {
  const { store: account } = useAccountStore();
  const { control, setValue, getValues } = useSaveFormContext();

  return (
    <View className="ml-4 gap-2 py-4">
      <View className="flex-row items-center gap-1">
        <Feather
          name={icons.tag.web}
          size={16}
          className="text-text-secondary pl-4"
        />
        <Text className="text-text-secondary font-semibold uppercase tracking-wide">
          Tags
        </Text>
      </View>
      <View className="pb-3 pl-4 pt-1">
        <NewCollectionOrTagForm
          type="tag"
          onSubmit={(data) => {
            const existingTag = account
              .spaceById(spaceId)
              ?.tagByValue(data.title);
            if (existingTag) {
              setValue(
                "space.tags",
                [existingTag.id].concat(getValues("space.tags")),
              );
              return;
            }

            const tagId = generateObjectId();
            account.createTag({
              set: {
                id: tagId,
                value: data.title,
              },
              spaceId,
            });
            setValue("space.tags", [tagId].concat(getValues("space.tags")));
          }}
        />
      </View>
      <Controller
        control={control}
        name="space.tags"
        render={({ field }) => {
          return (
            <View className="flex flex-row flex-wrap gap-x-2 gap-y-3 pl-4">
              {account.spaceById(spaceId)?.tagsAsArray?.map((tag) => {
                const isChecked = field.value?.includes(tag.id);
                return (
                  <SelectablePill
                    key={tag.id}
                    onPress={() => {
                      const tags = Array.isArray(field.value)
                        ? [...field.value]
                        : [];
                      const updatedTags = isChecked
                        ? tags.filter((tagId) => tagId !== tag.id)
                        : [...tags, tag.id];
                      field.onChange(updatedTags);
                    }}
                    isChecked={isChecked}
                    iconAfter={
                      isChecked
                        ? {
                            web: "x-circle",
                          }
                        : undefined
                    }
                  >
                    {tag.value}
                  </SelectablePill>
                );
              })}
            </View>
          );
        }}
      />
    </View>
  );
});

// TODO: Refactor these to be independent of parent form.
// moveTo and copyTo uses these too
export const RenderSpace = observer(function RenderSpace({
  spaceId,
}: {
  spaceId: string;
}) {
  const { store: account } = useAccountStore();
  const space = account.spaceById(spaceId);
  const { control } = useSaveFormContext();

  if (!space) return null;

  return (
    <View className="web:-mx-2">
      <Controller
        name="space.id"
        control={control}
        render={({ field }) => {
          const isChecked = field.value === space.id;

          return (
            <>
              <ListItem asChild>
                <Pressable
                  onPress={() => {
                    if (isChecked) {
                      field.onChange("");
                    } else {
                      field.onChange(spaceId);
                    }
                  }}
                  className="flex-row"
                >
                  <View className="flex-row items-center gap-2">
                    <Checkbox
                      className="overflow-hidden rounded-full"
                      value={isChecked}
                      color={
                        isChecked
                          ? colors.button.background.primary
                          : colors.button.background.secondary
                      }
                    />
                    <Text className="text-base text-white">
                      {space.displayName ?? space.slug}
                    </Text>
                  </View>
                </Pressable>
              </ListItem>
              {isChecked && (
                <>
                  <View className="border-grey-dark-5 border-b" />
                  <RenderCollections spaceId={spaceId} />
                </>
              )}

              {isChecked && (
                <>
                  <View className="border-grey-dark-5 border-b" />
                  <RenderTags spaceId={spaceId} />
                </>
              )}
            </>
          );
        }}
      />
    </View>
  );
});

function NewCollectionOrTagForm({
  onSubmit,
  type: type,
}: {
  onSubmit: (args: { title: string }) => void;
  type: "collection" | "tag";
}) {
  const { control, handleSubmit, setValue } = useForm({
    shouldFocusError: true,
    resolver: zodResolver(
      z.object({
        title: z.string().min(1),
      }),
    ),
    defaultValues: {
      title: "",
    },
  });

  const onFormSubmit = handleSubmit((data) => {
    onSubmit(data);
    setValue("title", "");
  });

  return (
    <Form className="flex-row justify-start gap-2" onSubmit={onFormSubmit}>
      <Controller
        control={control}
        name="title"
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            placeholder={type === "collection" ? "New collection" : "New tag"}
            onChangeText={field.onChange}
            value={field.value}
            errorMessage={fieldState.error?.message}
            size={"small"}
          />
        )}
      />

      <Button
        size="small"
        variant="outline"
        iconBefore={<Feather name={icons.create.web} size={16} />}
        onPress={onFormSubmit}
      >
        <Text className="text-white">
          {type === "collection" ? "Create Collection" : "Create Tag"}
        </Text>
      </Button>
    </Form>
  );
}
